/**
 * Tenant Context
 * Provides tenant configuration and theme to the application
 * Handles multi-tenancy awareness for the frontend
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from '../utils/apiClient';
import { usePermissions } from './PermissionContext';
import { getAvailableThemes, setTenantThemeConfig } from '../utils/themeSelector';
import { generateThemeMetadata } from '../utils/themeGenerator';
import logger from '../utils/logger';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

/**
 * Theme used only while no tenant has resolved yet (initial paint) so MUI has
 * something to render against. It carries NO tenant identity — deliberately no
 * id/code/name.
 *
 * There is intentionally no "default tenant" object here. A previous version fell
 * back to { id: 1, code: 'master', name: 'Master' } whenever the config call failed,
 * on the assumption that "Master is always ID 1 in production". That assumption is
 * FALSE in production, where id 1 is an operating tenant — so the fallback silently
 * mislabelled a real tenant as Master, which hid every business module in the sidebar
 * while the API (reading the untouched localStorage tenant id) kept serving that
 * tenant's data correctly. Failing to resolve a tenant is now surfaced, never guessed.
 */
const FALLBACK_THEME = {
  name: 'default',
  primaryColor: '#1976d2',
  secondaryColor: '#dc004e',
  logoUrl: null,
};

/**
 * Master tenant configuration - has access to all modules including tenant management
 */
const MASTER_TENANT_MODULES = [
  'capital_management',
  'contract_management',
  'payments',
  'stakeholder_management',
  'reports',
  'reference_data',
  'user_management',
  'admin_settings',
  'tenant_management',
  'amortization_schedules',
];

export const TenantProvider = ({ children }) => {
  // PermissionProvider wraps TenantProvider (see App.js), so this is safe here.
  // NOTE: usePermissions().user is a derived object literal and is ALWAYS truthy —
  // it is not a usable "is the user known yet" signal. Gate on isAuthenticated plus
  // the permission-load completing instead.
  const { isAuthenticated, loading: authLoading, hasNoTenantAccess } = usePermissions();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-tenant workflow document requirements (workflow_code -> is_required).
  // Loaded once per tenant and consulted by upload dialogs to decide whether
  // to render the "submit without document" affordance.
  const [workflowDocReqs, setWorkflowDocReqs] = useState(null);

  /**
   * Load tenant configuration from API
   */
  const loadTenantConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch current tenant config
      const response = await apiGet('/tenants/config/current');

      if (response.success && response.data) {
        const tenantData = response.data;

        // If this is the master tenant, ensure all admin modules are enabled.
        // isMaster is decided server-side (isMasterTenantCode in
        // middleware/requireOperatingTenant.js) so the client never infers
        // master-ness from a code string it may have guessed.
        if (tenantData.isMaster === true) {
          tenantData.modules = MASTER_TENANT_MODULES;
        }

        setTenant(tenantData);

        // IMPORTANT: Sync localStorage with the resolved tenant to prevent desync
        // This ensures subsequent API calls use the same tenant the API resolved
        const currentSelectedId = localStorage.getItem('selectedTenantId');
        if (tenantData.id && currentSelectedId !== String(tenantData.id)) {
          // Only update if different - prevents unnecessary writes
          // Use tenant ID (not code) for consistency with X-Tenant-ID header
          localStorage.setItem('selectedTenantId', String(tenantData.id));
          logger.debug('Synced selectedTenantId', { event: 'tenant_sync', code: tenantData.code });
        }

        // Cache the tenant theme for themeSelector to use
        if (tenantData.theme) {
          try {
            setTenantThemeConfig(tenantData.theme);
          } catch (themeErr) {
            logger.warn('Failed to set tenant theme config', { message: themeErr.message });
          }
        }
        logger.info('Tenant loaded', { event: 'tenant_loaded', code: tenantData.code });
      } else {
        // The API resolved no tenant for this user (req.tenant was falsy — e.g. the
        // tenant row is not 'active', or the user has no active assignment).
        // Surface it: substituting a placeholder identity here is what previously
        // painted a real tenant as "Master" and emptied the sidebar.
        setTenant(null);
        setError(new Error('No tenant is available for your account.'));
        logger.error('Tenant config unresolved — API returned no tenant context', {
          event: 'tenant_unresolved',
          reason: 'no_tenant_in_response',
        });
      }
    } catch (err) {
      // Includes the 401 that occurs if this fires before the auth token is in
      // storage. Never fabricate a tenant — show the error and offer a retry.
      setTenant(null);
      setError(err);
      logger.error('Tenant config load failed', {
        event: 'tenant_unresolved',
        reason: 'request_failed',
        status: err?.response?.status,
        message: err?.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load tenant config only once authentication has settled. This closes the race
   * where the request fired before the auth token reached storage, 401'd, and landed
   * in the error path on every cold load.
   *
   * Must also clear `loading` on the paths that will never produce a tenant
   * (unauthenticated, or authenticated with no tenant access) — otherwise the
   * consumers' `loading` gate would spin forever.
   */
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || hasNoTenantAccess) {
      setTenant(null);
      setLoading(false);
      return;
    }

    loadTenantConfig();
  }, [authLoading, isAuthenticated, hasNoTenantAccess, loadTenantConfig]);

  /**
   * Load workflow document requirements once tenant resolves. Failure here
   * is non-fatal — every workflow simply falls back to "not required" until
   * the data lands, which mirrors the seed defaults (only contract_submit is
   * mandatory by default and that's enforced on the server side anyway).
   */
  useEffect(() => {
    if (!tenant?.id) return;
    let cancelled = false;
    apiGet('/workflow-document-requirements')
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          const map = {};
          res.data.forEach((row) => { map[row.workflow_code] = row.is_required === true; });
          setWorkflowDocReqs(map);
        } else {
          setWorkflowDocReqs({});
        }
      })
      .catch(() => { if (!cancelled) setWorkflowDocReqs({}); });
    return () => { cancelled = true; };
  }, [tenant?.id]);

  /**
   * Returns true when the named workflow currently requires a document
   * upload. Defaults to false while the requirements map is still loading
   * or when the workflow code is not registered.
   */
  const isWorkflowDocumentRequired = useCallback((workflowCode) => {
    if (!workflowCode || !workflowDocReqs) return false;
    return workflowDocReqs[workflowCode] === true;
  }, [workflowDocReqs]);

  /**
   * Check if a module is enabled for this tenant
   */
  const isModuleEnabled = useCallback((moduleCode) => {
    if (!tenant?.modules) return true; // Default to enabled
    return tenant.modules.includes(moduleCode);
  }, [tenant]);

  /**
   * Check if a specific sub-feature is enabled for a module.
   * Defaults to enabled if no config exists (backwards compatible).
   * When a parent key (e.g. "brokers") is false but child keys
   * (e.g. "brokers.basic_information") are true, the parent is
   * treated as enabled so tabs/sections remain visible.
   */
  const isFeatureEnabled = useCallback((moduleCode, featureKey) => {
    if (!tenant?.modules) return true;
    if (!tenant.modules.includes(moduleCode)) return false;
    if (!tenant.moduleConfig?.[moduleCode]) return true;

    const config = tenant.moduleConfig[moduleCode];

    // If the direct key is not explicitly false, it's enabled
    if (config[featureKey] !== false) return true;

    // Direct key is false — check if ANY child features are still enabled
    const prefix = featureKey + '.';
    return Object.entries(config).some(
      ([key, val]) => key.startsWith(prefix) && val === true
    );
  }, [tenant]);

  /**
   * Get list of enabled modules
   */
  const getEnabledModules = useCallback(() => {
    // No tenant resolved => no modules. Returning a hardcoded list here would
    // re-introduce the "pretend we know the tenant" failure mode.
    return tenant?.modules || [];
  }, [tenant]);

  /**
   * Resolve a per-tenant terminology label by key, falling back when unset.
   * Backed by tenant_settings rows with key like 'stakeholder.<type>.label'.
   */
  const getStakeholderLabel = useCallback(
    (typeKey, fallback) => tenant?.terminology?.[typeKey] || fallback,
    [tenant]
  );

  /**
   * Get tenant theme configuration
   */
  const getTheme = useCallback(() => {
    // Neutral theme only — carries no tenant identity.
    return tenant?.theme || FALLBACK_THEME;
  }, [tenant]);


  /**
   * Get available themes for user profile selection
   * Order: Default, Dark Mode, then Tenant theme (if configured)
   */
  const availableThemes = useMemo(() => {
    try {
      const defaultThemes = getAvailableThemes(); // Returns [Default, Dark Mode]

      // If tenant has a custom theme configuration, add it after Dark Mode
      if (tenant?.theme?.primaryColor) {
        const tenantThemeMeta = generateThemeMetadata(tenant.theme, tenant.name);
        if (tenantThemeMeta) {
          // Order: Default, Dark Mode, Tenant theme
          return [...defaultThemes, tenantThemeMeta];
        }
      }

      return defaultThemes;
    } catch (err) {
      logger.warn('Failed to compute available themes', { message: err.message });
      return getAvailableThemes();
    }
  }, [tenant]);

  /**
   * Switch to a different tenant (for super-admins)
   * Note: The primary tenant switching is handled in Navbar.js using setSelectedTenantId()
   * This function is kept for backwards compatibility but should use the same key
   */
  const switchTenant = useCallback(async (tenantId) => {
    try {
      setLoading(true);
      // Store tenant preference using the correct localStorage key
      // This must match what apiClient.js reads (selectedTenantId)
      localStorage.setItem('selectedTenantId', tenantId);
      // Reload the page to apply new tenant context
      window.location.reload();
    } catch (err) {
      setError('Failed to switch tenant');
      setLoading(false);
    }
  }, []);

  const value = {
    tenant,
    loading,
    error,
    isModuleEnabled,
    isFeatureEnabled,
    getEnabledModules,
    getTheme,
    availableThemes,
    terminology: tenant?.terminology || {},
    getStakeholderLabel,
    isWorkflowDocumentRequired,
    switchTenant,
    reload: loadTenantConfig,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * HOC to require a specific module
 * Wraps a component and only renders if the module is enabled
 */
export const withModuleRequired = (WrappedComponent, moduleCode) => {
  return function ModuleRequiredComponent(props) {
    const { isModuleEnabled } = useTenant();

    if (!isModuleEnabled(moduleCode)) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Feature Not Available</h2>
          <p>This feature is not available for your organization.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Component that only renders children if module is enabled
 */
export const ModuleGate = ({ module, children, fallback = null }) => {
  const { isModuleEnabled, loading } = useTenant();

  if (loading) {
    return null;
  }

  if (!isModuleEnabled(module)) {
    return fallback;
  }

  return children;
};

export default TenantContext;
